import React from 'react';
import { rupees } from '@/components/myPay/i18n';

const Block = ({ title, total, children, testId }) => (
  <div className="rounded-xl border border-[#E5DFD0] bg-white" data-testid={testId}>
    <div className="flex items-center justify-between border-b bg-[#F7F3EA] px-4 py-2">
      <span className="text-sm font-semibold text-[#1A1F2E]">{title}</span>
      <span className="font-semibold tabular-nums" data-testid={`${testId}-total`}>{total}</span>
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);
const th = 'px-3 py-1.5 text-left text-[11px] font-medium uppercase text-gray-500';
const td = 'px-3 py-1.5 tabular-nums';

const Piece = ({ p, t }) => (
  <Block title={`${t('pieceRate')} · ${t('daysWorked', { n: p.days })}`} total={rupees(p.total)} testId="mypay-piece">
    <table className="w-full text-sm">
      <thead><tr><th className={th}>{t('work')}</th><th className={`${th} text-right`}>{t('qty')}</th><th className={`${th} text-right`}>{t('rate')}</th><th className={`${th} text-right`}>{t('amount')}</th></tr></thead>
      <tbody>{p.lines.map((l, i) => (
        <tr key={i} className="border-t" data-testid={`mypay-piece-line-${i}`}>
          <td className="px-3 py-1.5">{l.operation} <span className="text-xs text-gray-500">{l.module} · {l.unit_type}</span></td>
          <td className={`${td} text-right`}>{l.qty}</td><td className={`${td} text-right`}>{rupees(l.unit_rate)}</td><td className={`${td} text-right font-medium`}>{rupees(l.amount)}</td>
        </tr>
      ))}</tbody>
    </table>
  </Block>
);

const Supervisor = ({ rows, t }) => (
  <Block title={t('supervisor')} total={rupees(rows.reduce((s, r) => s + r.total_amount, 0))} testId="mypay-supervisor">
    <table className="w-full text-sm">
      <thead><tr><th className={th}>{t('work')}</th><th className={`${th} text-right`}>{t('days')}</th><th className={`${th} text-right`}>{t('wage')}</th><th className={`${th} text-right`}>{t('incentive')}</th><th className={`${th} text-right`}>{t('amount')}</th></tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i} className="border-t" data-testid={`mypay-sup-line-${i}`}>
          <td className="px-3 py-1.5">{r.operation} <span className="text-xs text-gray-500">{r.module}</span></td><td className={`${td} text-right`}>{r.days_worked}</td>
          <td className={`${td} text-right`}>{rupees(r.base_wage)}</td><td className={`${td} text-right`}>{rupees(r.total_incentive)}</td><td className={`${td} text-right font-medium`}>{rupees(r.total_amount)}</td>
        </tr>
      ))}</tbody>
    </table>
  </Block>
);

const Contract = ({ c, t }) => (
  <Block title={`${t('contract')} · ${t('agency')}: ${c.agency}`} total={c.amount == null ? t('rateNotSet') : rupees(c.amount)} testId="mypay-contract">
    <table className="w-full text-sm">
      <thead><tr><th className={th}>{t('section')}</th><th className={`${th} text-right`}>{t('days')}</th><th className={`${th} text-right`}>{t('truckDays')}</th><th className={`${th} text-right`}>{t('unloadHours')}</th><th className={`${th} text-right`}>{t('amount')}</th></tr></thead>
      <tbody>{c.sections.map((s, i) => (
        <tr key={i} className="border-t" data-testid={`mypay-contract-line-${i}`}>
          <td className="px-3 py-1.5">{s.section}</td><td className={`${td} text-right`}>{s.days}</td><td className={`${td} text-right`}>{s.truck_days}</td>
          <td className={`${td} text-right`}>{s.unload_hours}</td><td className={`${td} text-right font-medium`}>{s.amount == null ? '—' : rupees(s.amount)}</td>
        </tr>
      ))}</tbody>
    </table>
    <p className="px-3 py-1.5 text-xs text-gray-500">{t('rate')}: {c.day_rate != null ? `${rupees(c.day_rate)} ${t('perDay')}` : t('rateNotSet')}
      {c.hr_pending > 0 && <span className="ml-2 font-medium text-amber-700" data-testid="mypay-hr-pending">· {t('hrPending', { n: c.hr_pending })}</span>}</p>
  </Block>
);

const Fixed = ({ f, t }) => (
  <Block title={`${t('fixedPay')} · ${rupees(f.fixed_pay)} ${t('perMonth')}`} total={rupees(f.amount)} testId="mypay-fixed">
    <table className="w-full text-sm">
      <thead><tr><th className={th}>{t('work')}</th><th className={`${th} text-right`}>{t('qty')}</th><th className={`${th} text-right`}>{t('rate')}</th><th className={`${th} text-right`}>{t('amount')}</th></tr></thead>
      <tbody>
        <tr className="border-t" data-testid="mypay-fixed-day"><td className="px-3 py-1.5">{t('dayShift')}</td><td className={`${td} text-right`}>{f.days} {t('dayUnit')}</td>
          <td className={`${td} text-right`}>{rupees(f.per_day)} {t('perDay')}</td><td className={`${td} text-right font-medium`}>{rupees(f.day_amount)}</td></tr>
        <tr className="border-t" data-testid="mypay-fixed-night"><td className="px-3 py-1.5">{t('nightShift')}</td><td className={`${td} text-right`}>{f.night_hours} {t('hours')}</td>
          <td className={`${td} text-right`}>{rupees(f.night_rate)} {t('perHour')}</td><td className={`${td} text-right font-medium`}>{rupees(f.night_amount)}</td></tr>
      </tbody>
    </table>
    <p className="px-3 py-1.5 text-xs text-gray-500">{t('fixedNote', { n: f.divisor })}
      {f.hr_pending > 0 && <span className="ml-2 font-medium text-amber-700" data-testid="mypay-fixed-hr-pending">· {t('hrPending', { n: f.hr_pending })}</span>}
      {f.fix_checkout > 0 && <span className="ml-2 font-medium text-rose-700" data-testid="mypay-fixed-fix">· {t('fixPending', { n: f.fix_checkout })}</span>}</p>
  </Block>
);

/** Every pay that applies to the staff member: Fixed pay, or piece-rate lines + supervisor wage & incentive, or contract day pay — and the total. */
export const PaySection = ({ pay, t }) => {
  const none = !pay.piece_rate && !pay.supervisor.length && !pay.contract && !pay.fixed;
  return (
    <div className="space-y-3" data-testid="mypay-pay">
      {none && <p className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-center text-sm text-gray-500" data-testid="mypay-no-pay">{t('noPay')}</p>}
      {pay.fixed && <Fixed f={pay.fixed} t={t} />}
      {pay.piece_rate && <Piece p={pay.piece_rate} t={t} />}
      {pay.supervisor.length > 0 && <Supervisor rows={pay.supervisor} t={t} />}
      {pay.contract && <Contract c={pay.contract} t={t} />}
      {!none && (
        <div className="flex items-center justify-between rounded-xl bg-[#1A1F2E] px-4 py-3 text-white">
          <span className="font-semibold">{t('total')}</span><span className="text-xl font-bold tabular-nums" data-testid="mypay-total">{rupees(pay.total)}</span>
        </div>
      )}
    </div>
  );
};

export default PaySection;
